import React, { useState, useEffect } from 'react';
import { Search, Eye, Download, CheckCircle, XCircle, Clock, Filter, X } from 'lucide-react';
import { admissionsService } from '../../firebase/admissions';
import { generateApplicationReceiptBlob } from '../../utils/receiptGenerator';
import InbuiltPDFViewer from '../../components/InbuiltPDFViewer';
import toast from 'react-hot-toast';

const AllApplications = () => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('all'); // all, studentName, studentId, institutionName, institutionId, transactionId
  const [viewReceiptState, setViewReceiptState] = useState({ open: false, url: null, title: null });

  useEffect(() => {
    loadApplications();
  }, []);

  useEffect(() => {
    filterApplications();
  }, [searchTerm, searchType, applications]);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const result = await admissionsService.getAllApplications();
      if (result.success) {
        const apps = result.applications || [];
        
        // Load institution names for each application
        const appsWithInstitutionNames = await Promise.all(
          apps.map(async (app) => {
            if (app.institutionId) {
              try {
                const instResult = await admissionsService.getInstitutionById(app.institutionId);
                if (instResult.success && instResult.institution) {
                  return {
                    ...app,
                    institutionName: instResult.institution.name,
                    institutionType: instResult.institution.type
                  };
                }
              } catch (error) {
              }
            }
            return app;
          })
        );
        
        setApplications(appsWithInstitutionNames);
      } else {
        toast.error('Failed to load applications: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Error loading applications: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filterApplications = () => {
    if (!searchTerm.trim()) {
      setFilteredApplications(applications);
      return;
    }

    const term = searchTerm.toLowerCase().trim();
    let filtered = [...applications];

    switch (searchType) {
      case 'studentName':
        filtered = filtered.filter(app => 
          `${app.firstName} ${app.lastName}`.toLowerCase().includes(term)
        );
        break;
      case 'studentId':
        filtered = filtered.filter(app => 
          app.userId?.toLowerCase().includes(term) || app.id?.toLowerCase().includes(term)
        );
        break;
      case 'institutionName':
        filtered = filtered.filter(app => 
          app.institutionName?.toLowerCase().includes(term)
        );
        break;
      case 'institutionId':
        filtered = filtered.filter(app => 
          app.institutionId?.toLowerCase().includes(term)
        );
        break;
      case 'transactionId':
        filtered = filtered.filter(app => 
          app.paymentTransactionId?.toLowerCase().includes(term)
        );
        break;
      default:
        // Search all fields
        filtered = filtered.filter(app => 
          `${app.firstName} ${app.lastName}`.toLowerCase().includes(term) ||
          app.userId?.toLowerCase().includes(term) ||
          app.id?.toLowerCase().includes(term) ||
          app.institutionName?.toLowerCase().includes(term) ||
          app.institutionId?.toLowerCase().includes(term) ||
          app.paymentTransactionId?.toLowerCase().includes(term) ||
          app.email?.toLowerCase().includes(term) ||
          app.phone?.toLowerCase().includes(term)
        );
    }

    setFilteredApplications(filtered);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', label: 'Pending' },
      approved: { icon: CheckCircle, color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Approved' },
      admitted: { icon: CheckCircle, color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Admitted' },
      rejected: { icon: XCircle, color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', label: 'Rejected' },
      refund_pending: { icon: Clock, color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200', label: 'Refund Pending' },
      refunded: { icon: CheckCircle, color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Refunded' },
      submitted: { icon: Clock, color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', label: 'Submitted' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`${badge.color} px-3 py-1 rounded-full text-sm flex items-center gap-1`}>
        <Icon className="w-4 h-4" />
        {badge.label}
      </span>
    );
  };

  const handleViewReceipt = async (application) => {
    try {
      // Load institution data
      const instResult = await admissionsService.getInstitutionById(application.institutionId);
      if (!instResult.success) {
        toast.error('Failed to load institution data');
        return;
      }

      const institution = instResult.institution;
      
      // Generate receipt as blob URL for viewing
      const pdfUrl = await generateApplicationReceiptBlob(
        application,
        institution,
        application.paymentTransactionId || 'N/A',
        {
          amount: application.paymentAmount || institution.applicationFee || 0,
          status: application.paymentStatus || 'completed'
        }
      );
      
      setViewReceiptState({
        open: true,
        url: pdfUrl,
        title: `Receipt - ${application.firstName} ${application.lastName}`
      });
    } catch (error) {
      toast.error('Failed to generate receipt: ' + error.message);
    }
  };

  const handleDownloadReceipt = async (application) => {
    try {
      // Load institution data
      const instResult = await admissionsService.getInstitutionById(application.institutionId);
      if (!instResult.success) {
        toast.error('Failed to load institution data');
        return;
      }

      const institution = instResult.institution;
      
      // Generate receipt
      const { generateApplicationReceipt } = await import('../../utils/receiptGenerator');
      await generateApplicationReceipt(
        application,
        institution,
        application.paymentTransactionId || 'N/A',
        {
          amount: application.paymentAmount || institution.applicationFee || 0,
          status: application.paymentStatus || 'completed'
        }
      );
      
      toast.success('Receipt downloaded successfully');
    } catch (error) {
      toast.error('Failed to generate receipt: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">All Applications</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          View and manage all student applications across all institutions
        </p>
      </div>

      {/* Search and Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search applications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="text-gray-400 w-5 h-5" />
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Fields</option>
              <option value="studentName">Student Name</option>
              <option value="studentId">Student ID</option>
              <option value="institutionName">Institution Name</option>
              <option value="institutionId">Institution ID</option>
              <option value="transactionId">Transaction ID</option>
            </select>
          </div>
        </div>
        {searchTerm && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Found {filteredApplications.length} application(s)
          </p>
        )}
      </div>

      {/* Applications Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        {filteredApplications.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-600 dark:text-gray-400">
              {searchTerm ? 'No applications found matching your search' : 'No applications yet'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Student</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Institution</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Transaction ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredApplications.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {app.firstName} {app.lastName}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        ID: {app.userId?.substring(0, 8)}...
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {app.institutionName || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {app.institutionType ? app.institutionType.charAt(0).toUpperCase() + app.institutionType.slice(1) : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {app.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {app.phone}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(app.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      ₹{app.paymentAmount || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-gray-600 dark:text-gray-400 font-mono">
                        {app.paymentTransactionId ? (
                          <span className="cursor-pointer hover:text-blue-600" title={app.paymentTransactionId}>
                            {app.paymentTransactionId.substring(0, 12)}...
                          </span>
                        ) : (
                          'N/A'
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {app.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        {app.paymentTransactionId && (
                          <>
                            <button
                              onClick={() => handleViewReceipt(app)}
                              className="text-blue-600 hover:text-blue-800"
                              title="View Receipt"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDownloadReceipt(app)}
                              className="text-green-600 hover:text-green-800"
                              title="Download Receipt"
                            >
                              <Download className="w-5 h-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* PDF Viewer Modal for Receipt */}
      {viewReceiptState.open && viewReceiptState.url && (
        <InbuiltPDFViewer
          url={viewReceiptState.url}
          title={viewReceiptState.title}
          onClose={() => {
            // Clean up the blob URL
            if (viewReceiptState.url && viewReceiptState.url.startsWith('blob:')) {
              URL.revokeObjectURL(viewReceiptState.url);
            }
            setViewReceiptState({ open: false, url: null, title: null });
          }}
        />
      )}
    </div>
  );
};

export default AllApplications;

